import { useParams } from 'react-router-dom';
import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { HotTable } from '@handsontable/react';
import 'handsontable/dist/handsontable.full.min.css';
import '../../../styles/handsontable.css';
import { Box, Flex, useToast, Button, Text, HStack, useDisclosure, Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton } from '@chakra-ui/react';
import SubmitButton from '../../../CommonComponents/SubmitButton';
import { DataNotFound } from '../../../CommonComponents/DataNotFound';
import FormLoader from '../../../CommonComponents/FormLoader';
import Pagination from '../../DeriveFile/Pagination';
import { getEventScreeningsData, updateEventScreeningsData, deleteEventScreeningsData, createEventScreeningsData } from '../../../services/EventMasterlist';
import { showDeletedToast, showErrorToast, showInfoToast, showSuccessToast } from '../../../util/errorHandler';


const Screenings = ({ onCountLoad, ...props }) => {
  const { alias } = useParams();
  const [columnHeaders, setColumnHeaders] = useState([]);
  const [tableData, setTableData] = useState([]);

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [changedRecords, setChangedRecords] = useState(new Map());
  const [newRecords, setNewRecords] = useState(new Map());
  const [deletingRows, setDeletingRows] = useState(new Set());
  const [rowToDelete, setRowToDelete] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalRecords, setTotalRecords] = useState(0);

  const hotRef = useRef(null);
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleTableChange = useCallback((changes, source) => {
    if (source === 'edit' && changes) {
      const newChangedRecords = new Map(changedRecords);
      const newNewRecords = new Map(newRecords);

      changes.forEach(([row, col, oldValue, newValue]) => {
        if (col === 0 || col === 1 || col === columnHeaders.length + 1) return;
        if (oldValue === newValue) return;

        const columnHeader = columnHeaders[col - 1];
        const existingId = tableData[row]?.[1];

        if (!existingId) {
          // New row being filled in
          const existing = newNewRecords.get(row) || {};
          existing[columnHeader] = newValue;
          newNewRecords.set(row, existing);
        } else {
          // Existing row being edited
          const existing = newChangedRecords.get(row) || { [columnHeaders[0]]: existingId };
          existing[columnHeader] = newValue;
          newChangedRecords.set(row, existing);
        }
      });

      setChangedRecords(newChangedRecords);
      setNewRecords(newNewRecords);
    }
  }, [changedRecords, newRecords, columnHeaders, tableData]);

  const getTotalPages = useCallback(() => {
    return Math.ceil(totalRecords / pageSize);
  }, [totalRecords, pageSize]);

  const fetchData = useCallback(async (page = currentPage) => {
    try {
      const result = await getEventScreeningsData(alias, page, pageSize);
      if (result && result.data && result.data.length > 0) {
        const keys = Object.keys(result.data[0]);
        setColumnHeaders(keys);
        const rowData = result.data.map((item) => [
          null,
          ...keys.map((key) => item[key] ?? ''),
          null
        ]);
        setTotalRecords(result.total || rowData.length);
        setTableData(rowData);
      } else {
        setTotalRecords(0);
        setTableData([]);
      }
    } catch (error) {
      if (error?.response?.status !== 404) {
                showErrorToast(toast, "Failed to fetch data. Please try again.");
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alias, pageSize, currentPage]);

  const handlePageChange = useCallback((newPage) => {
    if (newPage >= 1 && newPage <= getTotalPages()) {
      setCurrentPage(newPage);
    }
  }, [getTotalPages]);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      await fetchData(currentPage);
      setIsLoading(false);
    };
    load();
  }, [fetchData, currentPage]);

  useEffect(() => {
    if (onCountLoad) onCountLoad(totalRecords);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalRecords]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const saveClickCallback = useCallback(async () => {
    const hasChanges = changedRecords.size > 0;
    const hasNewRecords = newRecords.size > 0;

    if (!hasChanges && !hasNewRecords) {
            showInfoToast(toast, "No changes to save");
      return;
    }

    setIsSubmitting(true);
    try {
      // POST new records
      if (hasNewRecords) {
        const newRows = Array.from(newRecords.values()).filter(row =>
          Object.values(row).some(v => v !== null && v !== '')
        );
        if (newRows.length > 0) {
          await createEventScreeningsData(alias, newRows);
        }
      }

      // PUT updated records
      if (hasChanges) {
        const updatedRows = Array.from(changedRecords.values());
        await updateEventScreeningsData(alias, updatedRows);
      }

            showSuccessToast(toast, "Data saved successfully");
      setChangedRecords(new Map());
      setNewRecords(new Map());
      await fetchData();
    } catch (error) {
            showErrorToast(toast, "Failed to save data. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changedRecords, newRecords, alias, fetchData, toast]);

  const handleDeleteRow = useCallback(async (rowIndex) => {
    const rowData = tableData[rowIndex];
    const recordId = rowData[1];

    if (!recordId) {
            showErrorToast(toast, "Cannot delete record without ID");
      return;
    }

    setDeletingRows(prev => new Set(prev).add(rowIndex));
    try {
      await deleteEventScreeningsData(alias, [{ [columnHeaders[0]]: recordId }]);

      const updated = new Map(changedRecords);
      updated.delete(rowIndex);
      setChangedRecords(updated);

            showDeletedToast(toast);

      // Re-fetch current page data from server
      await fetchData(currentPage);
    } catch (error) {
            showErrorToast(toast, "Failed to delete record. Please try again.");
    } finally {
      setDeletingRows(prev => {
        const s = new Set(prev);
        s.delete(rowIndex);
        return s;
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData, alias, columnHeaders, currentPage, changedRecords, fetchData, toast]);

  const confirmDelete = useCallback((rowIndex) => {
    const rowData = tableData[rowIndex];
    const recordId = rowData[1];
    setRowToDelete({ rowIndex, recordId, rowData });
    onOpen();
  }, [tableData, onOpen]);

  const handleConfirmedDelete = useCallback(() => {
    if (rowToDelete) {
      handleDeleteRow(rowToDelete.rowIndex);
      onClose();
      setRowToDelete(null);
    }
  }, [rowToDelete, onClose, handleDeleteRow]);

  const deleteColumnRenderer = useCallback((instance, td, row) => {
    td.innerHTML = '';
    const rowData = instance.getDataAtRow(row);
    const hasId = rowData && rowData[1];
    if (!hasId) return td;

    const isDeleting = deletingRows.has(row);
    const btn = document.createElement('button');
    btn.innerHTML = isDeleting ? '⏳' : '🗑️';
    btn.title = isDeleting ? 'Deleting...' : 'Delete this record';
    btn.className = isDeleting ? 'delete-button deleting' : 'delete-button';
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!isDeleting) confirmDelete(row);
    });
    td.appendChild(btn);
    return td;
  }, [deletingRows, confirmDelete]);

  const hotTableSettings = useMemo(() => ({
    data: tableData,
    colHeaders: columnHeaders.length > 0 ? ['S.No.', ...columnHeaders, 'Actions'] : ['S.No.'],
    height: 'auto',
    afterChange: handleTableChange,
    licenseKey: 'non-commercial-and-evaluation',
    autoWrapRow: true,
    contextMenu: true,

    cells: (row, col) => {
      if (col === 0) return { readOnly: true };
      if (col === 1) return { readOnly: true };
      if (col === columnHeaders.length + 1) return { renderer: deleteColumnRenderer };
      const columnHeader = columnHeaders[col - 1];
      if (columnHeader === 'alias') return { readOnly: true };
      return {};
    },
    hiddenColumns: { columns: [1], indicators: false },
    plugins: ['hiddenColumns'],
    columns: [
      {
        data: 0,
        title: 'S.No.',
        width: 60,
        readOnly: true,
        renderer: (instance, td, row) => {
          const rowData = instance.getDataAtRow(row);
          const hasId = rowData && rowData[1];
          td.style.textAlign = 'center';
          td.style.fontWeight = 'bold';
          td.style.backgroundColor = '#f8f9fa';
          td.style.borderRight = '2px solid #dee2e6';
          td.style.color = '#495057';
          td.innerHTML = hasId
            ? (currentPage - 1) * pageSize + row + 1
            : totalRecords + 1;
          return td;
        }
      },
      ...columnHeaders.map((header, index) => {
        const columnWidth = Math.min(header.length * 8 + 140, 300);
        return {
          data: index + 1,
          title: header,
          readOnly: index === 0 || header === 'alias',
          width: columnWidth,
          wordWrap: true,
          renderer: (instance, td, row, col, prop, value) => {
            td.style.whiteSpace = 'normal';
            td.style.wordWrap = 'break-word';
            td.style.maxWidth = `${columnWidth}px`;
            td.style.padding = '8px';
            td.style.verticalAlign = 'top';
            td.innerHTML = value || '';
            return td;
          }
        };
      }),
      { data: columnHeaders.length + 1, title: 'Actions', width: 80, renderer: deleteColumnRenderer }
    ]
  }), [tableData, columnHeaders, handleTableChange, deleteColumnRenderer, currentPage, pageSize, totalRecords]);

  const hasUnsavedChanges = changedRecords.size > 0 || newRecords.size > 0;

  return (
    <Box maxW="100%">
      {isLoading ? (
        <FormLoader />
      ) : (
        <>
          <Flex justifyContent="space-between" mb={4} alignItems="center">
            <HStack spacing={4} alignItems="center">
              <SubmitButton isLoading={isSubmitting} onClick={saveClickCallback} />
              <Text fontSize="sm" color="gray.600" fontWeight="medium">
                Total Records: <strong>{totalRecords}</strong>
              </Text>
              {hasUnsavedChanges && (
                <Box color="orange.500" fontSize="sm">Unsaved changes — Click Save to update</Box>
              )}
              {newRecords.size > 0 && (
                <Box color="blue.500" fontSize="sm">{newRecords.size} new row(s) to be created</Box>
              )}
            </HStack>
            {totalRecords > pageSize && (
              <Pagination handlePageChange={handlePageChange} currentPage={currentPage} totalPages={getTotalPages()} setPageSize={setPageSize} />
            )}
          </Flex>

          {columnHeaders.length > 0 ? (
            <>
              <HotTable ref={hotRef} {...hotTableSettings} />
              <Flex justifyContent="space-between" mt={4}>
                <SubmitButton isLoading={isSubmitting} onClick={saveClickCallback} />
              </Flex>
            </>
          ) : (
            <DataNotFound />
          )}
        </>
      )}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm Delete</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>Are you sure you want to delete this record?</Text>
            {rowToDelete && (
              <Box mt={3} p={3} bg="gray.50" borderRadius="md">
                <Text fontSize="sm" fontWeight="bold">Record Details:</Text>
                <Text fontSize="sm">ID: {rowToDelete.recordId}</Text>
                {columnHeaders.slice(1, 4).map((header, index) => (
                  <Text key={index} fontSize="sm">{header}: {rowToDelete.rowData[index + 2] || 'N/A'}</Text>
                ))}
              </Box>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
            <Button colorScheme="red" onClick={handleConfirmedDelete} isLoading={rowToDelete && deletingRows.has(rowToDelete.rowIndex)}>
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Screenings;
